import type { RemoteConfig } from "./types";

// v2 invalida entradas gravadas antes do contrato público incluir `assistant`.
const CACHE_PREFIX = "indexaai:metatag:config:v2:";
const CACHE_TTL_MS = 5 * 60 * 1000;
const NEGATIVE_CACHE_TTL_MS = 15 * 1000;
const INCOMPLETE_RESPONSE_RETRY_DELAYS_MS = [250, 750] as const;

interface CacheEntry {
  expiresAt: number;
  config: RemoteConfig;
}

interface RemoteConfigResponse extends Omit<RemoteConfig, "assistant"> {
  assistant?: RemoteConfig["assistant"];
}

function readCache(publicKey: string): CacheEntry | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + publicKey);
    if (!raw) return null;

    const entry = JSON.parse(raw) as CacheEntry;
    if (entry.expiresAt < Date.now() || !entry.config) return null;

    return entry;
  } catch {
    return null;
  }
}

function writeCache(publicKey: string, config: RemoteConfig): void {
  try {
    const ttl = config.assistant ? CACHE_TTL_MS : NEGATIVE_CACHE_TTL_MS;
    const entry: CacheEntry = { expiresAt: Date.now() + ttl, config };
    sessionStorage.setItem(CACHE_PREFIX + publicKey, JSON.stringify(entry));
  } catch {
    // sessionStorage indisponível (modo privado, quota etc.) — segue sem cache.
  }
}

function hasAssistantField(config: unknown): config is RemoteConfigResponse {
  return typeof config === "object" && config !== null && "assistant" in config;
}

function normalize(config: RemoteConfigResponse): RemoteConfig {
  return { ...config, assistant: config.assistant ?? null };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelayMs(attempt: number): number | null {
  return INCOMPLETE_RESPONSE_RETRY_DELAYS_MS[attempt] ?? null;
}

export async function fetchRemoteConfig(
  apiBase: string,
  publicKey: string,
): Promise<RemoteConfig | null> {
  const cached = readCache(publicKey);
  if (cached) return cached.config;

  for (let attempt = 0; attempt <= INCOMPLETE_RESPONSE_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const res = await fetch(`${apiBase}/v1/metatag/config`, {
        headers: { "x-indexaai-key": publicKey },
        cache: "no-store",
      });
      if (!res.ok) {
        const retryDelay = retryDelayMs(attempt);
        if (res.status >= 500 && retryDelay !== null) {
          await delay(retryDelay);
          continue;
        }
        return null;
      }

      const payload = (await res.json()) as unknown;
      if (!hasAssistantField(payload)) {
        // Durante rollout, uma réplica antiga pode responder sem o campo do agente.
        // Não transforme essa resposta incompleta em um cache negativo de longa duração.
        const retryDelay = retryDelayMs(attempt);
        if (retryDelay !== null) {
          await delay(retryDelay);
          continue;
        }

        return payload && typeof payload === "object"
          ? normalize(payload as RemoteConfigResponse)
          : null;
      }

      const config = normalize(payload);
      writeCache(publicKey, config);
      return config;
    } catch {
      const retryDelay = retryDelayMs(attempt);
      if (retryDelay !== null) {
        await delay(retryDelay);
        continue;
      }
      return null;
    }
  }

  return null;
}
